/**
 * SOV Dashboard — AI Share of Voice
 *
 * Shows how visible the site is across AI engines (ChatGPT, Perplexity, Gemini, Claude).
 * Content Managers use this to track AEO content effectiveness over time.
 *
 * Tabs:
 *   1. Overview — SOV score, provider breakdown, funnel breakdown
 *   2. Early Indicators — Knowledge loop, cluster authority, competitor displacement
 *   3. Monitoring Basket — The queries being tracked, their cadence, status
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  sovService,
  type SOVDashboardData,
  type SOVBasketQuery,
  type SOVBasketStats,
  type ClusterAuthorityRow,
  type CompetitorDisplacementRow,
  type SOVGroupData,
  type SOVCheckResult,
  type TopCompetitor,
  type CompetitorByQuery,
  type SOVCompetitorData,
  type CoverageBandData,
  type CoverageSummary,
} from '@/services/sovService';
import {
  Bot, TrendingUp, Eye, Target, Loader2, RefreshCw, Zap,
  ArrowRightLeft, Brain, Shield, AlertCircle, CheckCircle,
  BarChart3, Gauge, Play, Users, FileSearch
} from 'lucide-react';

// ============================================================================
// Constants
// ============================================================================

const PROVIDER_COLORS: Record<string, string> = {
  openai: '#10a37f',
  anthropic: '#d4a574',
  perplexity: '#20b2aa',
  google: '#4285f4',
};

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'ChatGPT',
  anthropic: 'Claude',
  perplexity: 'Perplexity',
  google: 'Gemini',
};

const FUNNEL_COLORS: Record<string, string> = {
  awareness: '#8b5cf6',
  consideration: '#3b82f6',
  purchase: '#10b981',
  trust: '#f59e0b',
};

const TIER_LABELS: Record<string, string> = {
  head_term: 'Head Terms',
  commercial_gap: 'Commercial Gaps',
  nl_question: 'NL Questions',
  competitor_flip: 'Competitor-Owned',
};

const CADENCE_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
};

// ============================================================================
// Main Component
// ============================================================================

export default function SOVDashboard() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id || (selectedSite as any)?._id;
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SOVDashboardData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [days, setDays] = useState('30');

  // Basket state
  const [basketLoading, setBasketLoading] = useState(false);
  const [basket, setBasket] = useState<SOVBasketQuery[]>([]);
  const [basketTotal, setBasketTotal] = useState(0);
  const [basketFilter, setBasketFilter] = useState<string>('all');

  // Results state
  const [resultsLoading, setResultsLoading] = useState(false);
  const [results, setResults] = useState<SOVCheckResult[]>([]);
  const [resultsTotal, setResultsTotal] = useState(0);

  // Competitors state
  const [competitorsLoading, setCompetitorsLoading] = useState(false);
  const [competitorData, setCompetitorData] = useState<SOVCompetitorData | null>(null);

  // Action states
  const [buildingBasket, setBuildingBasket] = useState(false);
  const [runningChecks, setRunningChecks] = useState(false);

  // Fetch SOV metrics
  const fetchMetrics = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const result = await sovService.getMetrics(siteId, parseInt(days));
      setData(result);
    } catch (error: any) {
      // Empty table is expected for new deployments
      if (error.message?.includes('500')) {
        setData(null);
      } else {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, days, toast]);

  // Fetch basket
  const fetchBasket = useCallback(async () => {
    if (!siteId) return;
    setBasketLoading(true);
    try {
      const tierFilter = basketFilter !== 'all' ? basketFilter : undefined;
      const result = await sovService.getBasket(siteId, { tier: tierFilter, limit: 100 });
      setBasket(result.queries);
      setBasketTotal(result.total);
    } catch {
      setBasket([]);
      setBasketTotal(0);
    } finally {
      setBasketLoading(false);
    }
  }, [siteId, basketFilter]);

  // Fetch results
  const fetchResults = useCallback(async () => {
    if (!siteId) return;
    setResultsLoading(true);
    try {
      const result = await sovService.getResults(siteId, parseInt(days), 100);
      setResults(result.results);
      setResultsTotal(result.total);
    } catch { setResults([]); }
    finally { setResultsLoading(false); }
  }, [siteId, days]);

  // Fetch competitors
  const fetchCompetitors = useCallback(async () => {
    if (!siteId) return;
    setCompetitorsLoading(true);
    try {
      const result = await sovService.getCompetitors(siteId, parseInt(days));
      setCompetitorData(result);
    } catch { setCompetitorData(null); }
    finally { setCompetitorsLoading(false); }
  }, [siteId, days]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);
  useEffect(() => { if (activeTab === 'basket') fetchBasket(); }, [activeTab, fetchBasket]);
  useEffect(() => { if (activeTab === 'results') fetchResults(); }, [activeTab, fetchResults]);
  useEffect(() => { if (activeTab === 'competitors') fetchCompetitors(); }, [activeTab, fetchCompetitors]);

  // Actions
  const handleBuildBasket = async () => {
    if (!siteId) return;
    setBuildingBasket(true);
    try {
      await sovService.buildBasket(siteId, { basketSize: 200 });
      toast({ title: 'Basket build started', description: 'Selecting 200 monitoring queries from demand signals. This takes ~30 seconds.' });
      // Refresh basket after a delay
      setTimeout(() => { fetchBasket(); }, 5000);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setBuildingBasket(false);
    }
  };

  const handleRunChecks = async () => {
    if (!siteId) return;
    setRunningChecks(true);
    try {
      await sovService.runChecks(siteId, { maxChecks: 20, force: false });
      toast({ title: 'SOV checks started', description: 'Running visibility checks across AI engines. Results will appear in a few minutes.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setRunningChecks(false);
    }
  };

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Select a site to view AI Share of Voice data.</AlertDescription></Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Share of Voice</h1>
          <p className="text-sm text-muted-foreground">
            Track how visible your site is across ChatGPT, Perplexity, Gemini, and Claude
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchMetrics} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={handleRunChecks} disabled={runningChecks || basketTotal === 0}>
            {runningChecks ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
            Run Checks
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="results">Check Results</TabsTrigger>
          <TabsTrigger value="competitors">Competitors</TabsTrigger>
          <TabsTrigger value="indicators">Early Indicators</TabsTrigger>
          <TabsTrigger value="basket">Monitoring Basket</TabsTrigger>
        </TabsList>

        {/* ================================================================ */}
        {/* TAB 1: OVERVIEW                                                  */}
        {/* ================================================================ */}
        <TabsContent value="overview" className="space-y-6">
          {loading ? <LoadingSkeleton /> : data ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-4 gap-4">
                <SOVScoreCard value={data.metrics.aeo_sov_pct} />
                <MetricCard
                  title="Total Checks"
                  value={String(data.metrics.total_checks)}
                  icon={Eye}
                  subtitle="queries checked"
                />
                <MetricCard
                  title="Brand Mentions"
                  value={String(data.metrics.total_mentions)}
                  icon={CheckCircle}
                  subtitle={`${data.metrics.raw_mention_rate || 0}% mention rate`}
                />
                <MetricCard
                  title="Basket Size"
                  value={String(basketTotal)}
                  icon={Target}
                  subtitle="queries monitored"
                />
              </div>

              {/* Provider Breakdown */}
              {data.metrics.sov_by_provider && Object.keys(data.metrics.sov_by_provider).length > 0 && (
                <div className="grid grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">SOV by AI Engine</CardTitle>
                      <CardDescription>Weighted share of voice per provider</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ProviderBarChart data={data.metrics.sov_by_provider} />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">SOV by Funnel Stage</CardTitle>
                      <CardDescription>Where in the buyer journey AI engines cite you</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {data.metrics.sov_by_funnel && Object.keys(data.metrics.sov_by_funnel).length > 0 ? (
                        <FunnelPieChart data={data.metrics.sov_by_funnel} />
                      ) : (
                        <EmptyState message="No funnel breakdown yet" />
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Deployed vs Control Comparison */}
              {/* Coverage Landscape */}
              {data.metrics.coverage_summary && (
                <CoverageLandscapeCard
                  summary={data.metrics.coverage_summary}
                  bands={data.metrics.sov_by_coverage || {}}
                />
              )}
            </>
          ) : (
            <ZeroState onBuildBasket={handleBuildBasket} building={buildingBasket} basketTotal={basketTotal} />
          )}
        </TabsContent>

        {/* ================================================================ */}
        {/* TAB 2: EARLY INDICATORS                                          */}
        {/* ================================================================ */}
        <TabsContent value="indicators" className="space-y-6">
          {loading ? <LoadingSkeleton /> : data ? (
            <>
              {/* Indicator 1: Knowledge Loop */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-purple-500" />
                    <CardTitle className="text-base">Knowledge Loop Completion</CardTitle>
                  </div>
                  <CardDescription>
                    Questions your chatbot couldn't answer that AI engines NOW answer citing you.
                    This proves the knowledge gap is closed end-to-end.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <KnowledgeLoopPanel data={data.indicators.knowledgeLoop} />
                </CardContent>
              </Card>

              {/* Indicator 2: Cluster Authority */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-blue-500" />
                    <CardTitle className="text-base">Cluster Authority Shift</CardTitle>
                  </div>
                  <CardDescription>
                    How often AI engines mention your brand per demand cluster.
                    Rising rates = growing semantic authority in that demand space.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ClusterAuthorityTable rows={data.indicators.clusterAuthority} />
                </CardContent>
              </Card>

              {/* Indicator 3: Competitor Displacement */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <ArrowRightLeft className="h-5 w-5 text-green-500" />
                    <CardTitle className="text-base">Competitor Displacement</CardTitle>
                  </div>
                  <CardDescription>
                    For questions competitors "owned" in AI answers — how often do AI engines
                    now cite you instead? This is the zero-sum proof of AEO effectiveness.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <CompetitorDisplacementTable rows={data.indicators.competitorDisplacement} />
                </CardContent>
              </Card>
            </>
          ) : (
            <ZeroState onBuildBasket={handleBuildBasket} building={buildingBasket} basketTotal={basketTotal} />
          )}
        </TabsContent>

        {/* ================================================================ */}
        {/* TAB: CHECK RESULTS                                               */}
        {/* ================================================================ */}
        <TabsContent value="results" className="space-y-6">
          {resultsLoading ? <LoadingSkeleton /> : results.length > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{resultsTotal} checks in the last {days} days</p>
              </div>
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Query</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Mentioned?</TableHead>
                      <TableHead>Quality</TableHead>
                      <TableHead>Competitors Cited</TableHead>
                      <TableHead>Context</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((r) => (
                      <TableRow key={r.id} className={r.site_mentioned ? 'bg-green-50/50' : ''}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(r.checked_at).toLocaleDateString()}<br/>
                          {new Date(r.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <p className="font-medium truncate text-sm" title={r.query_text}>{r.query_text}</p>
                          <div className="flex gap-1 mt-1">
                            {Number(r.aeo_proximity) > 0 && <ProximityBadge value={Number(r.aeo_proximity)} title={r.nearest_product_title} />}
                            {r.tier && <Badge variant="outline" className="text-[10px]">{TIER_LABELS[r.tier] || r.tier}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="text-xs" style={{ backgroundColor: PROVIDER_COLORS[r.llm_provider] || '#94a3b8', color: 'white' }}>
                            {PROVIDER_LABELS[r.llm_provider] || r.llm_provider}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {r.site_mentioned ? (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              Yes {r.mention_position === 1 ? '(#1)' : r.mention_position ? `(#${r.mention_position})` : ''}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">No</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {r.citation_quality ? (
                            <span className="text-sm">{['', 'Indirect', 'Listed', 'Recommended'][r.citation_quality] || r.citation_quality}</span>
                          ) : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {r.competitors_mentioned && r.competitors_mentioned.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {r.competitors_mentioned.slice(0, 4).map((c, i) => (
                                <Badge key={i} variant="outline" className="text-[10px]">{c}</Badge>
                              ))}
                              {r.competitors_mentioned.length > 4 && (
                                <Badge variant="outline" className="text-[10px]">+{r.competitors_mentioned.length - 4}</Badge>
                              )}
                            </div>
                          ) : <span className="text-xs text-muted-foreground">None</span>}
                        </TableCell>
                        <TableCell className="max-w-[250px] text-xs text-muted-foreground truncate" title={r.mention_context || ''}>
                          {r.mention_context || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FileSearch className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-lg font-medium mb-2">No check results yet</p>
                <p className="text-sm text-muted-foreground">Run SOV checks to see detailed results per query and provider.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ================================================================ */}
        {/* TAB: COMPETITORS                                                 */}
        {/* ================================================================ */}
        <TabsContent value="competitors" className="space-y-6">
          {competitorsLoading ? <LoadingSkeleton /> : competitorData ? (
            <>
              {/* Top competitors */}
              {competitorData.topCompetitors.length > 0 ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-orange-500" />
                      <CardTitle className="text-base">Who AI Engines Recommend Instead</CardTitle>
                    </div>
                    <CardDescription>
                      Brands and sites most frequently cited across all AI engine responses.
                      These are your competitors in the AI answer space.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Competitor</TableHead>
                          <TableHead className="text-right">Times Cited</TableHead>
                          <TableHead className="text-right">Across Queries</TableHead>
                          <TableHead>Cited By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {competitorData.topCompetitors.map((c) => (
                          <TableRow key={c.competitor}>
                            <TableCell className="font-medium">{c.competitor}</TableCell>
                            <TableCell className="text-right">{c.times_mentioned}</TableCell>
                            <TableCell className="text-right">{c.across_queries}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {c.by_providers.map((p, i) => (
                                  <Badge key={i} className="text-[10px]" style={{ backgroundColor: PROVIDER_COLORS[p] || '#94a3b8', color: 'white' }}>
                                    {PROVIDER_LABELS[p] || p}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-lg font-medium mb-2">No competitor data yet</p>
                    <p className="text-sm text-muted-foreground">Run more SOV checks — competitor mentions are extracted from AI responses.</p>
                  </CardContent>
                </Card>
              )}

              {/* Competitor by query detail */}
              {competitorData.competitorByQuery.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Query-Level Competitor Map</CardTitle>
                    <CardDescription>
                      For each query, which competitors did AI engines cite? Green rows = we were also mentioned.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Query</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>We Mentioned?</TableHead>
                          <TableHead>Our Rank</TableHead>
                          <TableHead>Competitors Cited</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {competitorData.competitorByQuery.map((row, i) => (
                          <TableRow key={i} className={row.site_mentioned ? 'bg-green-50/50' : ''}>
                            <TableCell className="font-medium max-w-[200px] truncate" title={row.query_text}>
                              {row.query_text}
                              {Number(row.aeo_proximity) > 0 && <ProximityBadge value={Number(row.aeo_proximity)} />}
                            </TableCell>
                            <TableCell>
                              <Badge className="text-xs" style={{ backgroundColor: PROVIDER_COLORS[row.llm_provider] || '#94a3b8', color: 'white' }}>
                                {PROVIDER_LABELS[row.llm_provider] || row.llm_provider}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {row.site_mentioned
                                ? <Badge className="bg-green-100 text-green-800 text-xs">Yes</Badge>
                                : <span className="text-xs text-muted-foreground">No</span>}
                            </TableCell>
                            <TableCell className="text-center">
                              {row.our_position_vs_competitors
                                ? <span className="font-medium">#{row.our_position_vs_competitors}</span>
                                : <span className="text-xs text-muted-foreground">-</span>}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {row.competitors_mentioned.slice(0, 5).map((c, j) => (
                                  <Badge key={j} variant="outline" className="text-[10px]">{c}</Badge>
                                ))}
                                {row.competitors_mentioned.length > 5 && (
                                  <Badge variant="outline" className="text-[10px]">+{row.competitors_mentioned.length - 5}</Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-lg font-medium mb-2">No competitor data yet</p>
                <p className="text-sm text-muted-foreground">Run SOV checks to see which competitors AI engines recommend.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ================================================================ */}
        {/* TAB: MONITORING BASKET                                           */}
        {/* ================================================================ */}
        <TabsContent value="basket" className="space-y-6">
          {/* Basket actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Select value={basketFilter} onValueChange={setBasketFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="head_term">Head Terms</SelectItem>
                  <SelectItem value="commercial_gap">Commercial Gaps</SelectItem>
                  <SelectItem value="nl_question">NL Questions</SelectItem>
                  <SelectItem value="competitor_flip">Competitor-Owned</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                {basketTotal} queries in basket
              </span>
            </div>
            <Button onClick={handleBuildBasket} disabled={buildingBasket} variant="outline" size="sm">
              {buildingBasket ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Rebuild Basket
            </Button>
          </div>

          {basketLoading ? <LoadingSkeleton /> : basket.length > 0 ? (
            <BasketTable queries={basket} />
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-lg font-medium mb-2">No monitoring basket yet</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Build a basket to select the top 200 queries from your demand signals for SOV tracking.
                </p>
                <Button onClick={handleBuildBasket} disabled={buildingBasket}>
                  {buildingBasket ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                  Build Monitoring Basket
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function SOVScoreCard({ value }: { value: number }) {
  const score = Number(value) || 0;
  const color = score >= 30 ? 'text-green-600' : score >= 10 ? 'text-yellow-600' : 'text-red-600';
  const bg = score >= 30 ? 'bg-green-50' : score >= 10 ? 'bg-yellow-50' : 'bg-red-50';

  return (
    <Card className={bg}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">AEO Share of Voice</p>
            <p className={`text-3xl font-bold ${color}`}>{score.toFixed(1)}%</p>
          </div>
          <Gauge className={`h-8 w-8 ${color}`} />
        </div>
        <Progress value={Math.min(score, 100)} className="mt-3 h-2" />
      </CardContent>
    </Card>
  );
}

function MetricCard({ title, value, icon: Icon, subtitle }: {
  title: string;
  value: string;
  icon: React.ElementType;
  subtitle?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

function ProviderBarChart({ data }: { data: Record<string, number> }) {
  const chartData = Object.entries(data).map(([provider, sov]) => ({
    provider: PROVIDER_LABELS[provider] || provider,
    sov: Number(sov) || 0,
    fill: PROVIDER_COLORS[provider] || '#94a3b8',
  }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={chartData} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <YAxis type="category" dataKey="provider" width={90} />
        <Tooltip formatter={(v: number) => `${Number(v).toFixed(1)}%`} />
        <Bar dataKey="sov" name="SOV %" radius={[0, 4, 4, 0]}>
          {chartData.map((entry, i) => (
            <Cell key={i} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function FunnelPieChart({ data }: { data: Record<string, number> }) {
  const chartData = Object.entries(data).map(([stage, sov]) => ({
    name: stage.charAt(0).toUpperCase() + stage.slice(1),
    value: Number(sov) || 0,
    fill: FUNNEL_COLORS[stage] || '#94a3b8',
  }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={3}
          dataKey="value"
          label={({ name, value }) => `${name}: ${Number(value).toFixed(1)}%`}
        >
          {chartData.map((entry, i) => (
            <Cell key={i} fill={entry.fill} />
          ))}
        </Pie>
        <Tooltip formatter={(v: number) => `${Number(v).toFixed(1)}%`} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function CoverageLandscapeCard({ summary, bands }: {
  summary: CoverageSummary;
  bands: Record<string, CoverageBandData>;
}) {
  const totalMarket = Number(summary.total_market_value_eur) || 0;
  const coveredValue = Number(summary.covered_value_eur) || 0;
  const gapValue = Number(summary.coverage_gap_eur) || 0;
  const avgProx = Number(summary.avg_proximity) || 0;
  const coveredClusters = Number(summary.covered_clusters) || 0;
  const totalClusters = Number(summary.total_clusters) || 0;
  const coveragePct = totalMarket > 0 ? Math.round(coveredValue / totalMarket * 100) : 0;

  const formatEur = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toFixed(0);

  // Stacked bar data
  const bandOrder = ['high', 'medium', 'low', 'none'] as const;
  const bandColors: Record<string, string> = { high: '#10b981', medium: '#3b82f6', low: '#f59e0b', none: '#d1d5db' };
  const bandLabels: Record<string, string> = { high: 'Strong', medium: 'Covered', low: 'Weak', none: 'Uncovered' };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-indigo-500" />
          <CardTitle className="text-base">AEO Market Coverage</CardTitle>
        </div>
        <CardDescription>
          Semantic proximity between demand clusters and deployed AEO product content.
          Higher proximity = AI engines have more signal to cite you.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* KPI tiles */}
        <div className="grid grid-cols-4 gap-4">
          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">Total Market</p>
            <p className="text-xl font-bold">{formatEur(totalMarket)} EUR</p>
            <p className="text-xs text-muted-foreground">{totalClusters} clusters</p>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50/50 p-3">
            <p className="text-xs text-green-700 font-medium">Covered</p>
            <p className="text-xl font-bold text-green-700">{formatEur(coveredValue)} EUR</p>
            <p className="text-xs text-muted-foreground">{coveredClusters} clusters ({coveragePct}%)</p>
          </div>
          <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3">
            <p className="text-xs text-orange-700 font-medium">Gap</p>
            <p className="text-xl font-bold text-orange-700">{formatEur(gapValue)} EUR</p>
            <p className="text-xs text-muted-foreground">{totalClusters - coveredClusters} clusters uncovered</p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">Avg Proximity</p>
            <p className="text-xl font-bold">{avgProx.toFixed(3)}</p>
            <Progress value={avgProx * 100} className="mt-1 h-2" />
          </div>
        </div>

        {/* Coverage distribution bar */}
        <div>
          <p className="text-xs text-muted-foreground mb-2">Coverage Distribution</p>
          <div className="flex h-8 rounded-md overflow-hidden">
            {bandOrder.map(band => {
              const b = bands[band];
              const count = b ? Number(b.checks) || 0 : 0;
              const totalChecks = Object.values(bands).reduce((s, v) => s + (Number(v.checks) || 0), 0);
              const pct = totalChecks > 0 ? (count / totalChecks * 100) : 0;
              if (pct === 0) return null;
              return (
                <div
                  key={band}
                  className="flex items-center justify-center text-xs font-medium text-white"
                  style={{ width: `${pct}%`, backgroundColor: bandColors[band], minWidth: pct > 5 ? 'auto' : '0' }}
                  title={`${bandLabels[band]}: ${count} checks, ${Number(b?.mention_rate || 0).toFixed(1)}% mention rate, ${Number(b?.total_value_eur || 0).toFixed(0)} EUR`}
                >
                  {pct > 8 ? bandLabels[band] : ''}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            {bandOrder.map(band => {
              const b = bands[band];
              if (!b) return null;
              return (
                <div key={band} className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: bandColors[band] }} />
                  {bandLabels[band]}: {Number(b.mention_rate || 0).toFixed(0)}% mentions
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KnowledgeLoopPanel({ data }: { data: { selfheal_queries_monitored: number; now_mentioned_in_ai: number; conversion_pct: number } }) {
  if (!data || data.selfheal_queries_monitored === 0) {
    return (
      <EmptyState message="No chatbot self-heal questions in the monitoring basket yet. These appear when your chatbot fails to answer a visitor question." />
    );
  }

  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="text-center">
        <p className="text-3xl font-bold">{data.selfheal_queries_monitored}</p>
        <p className="text-sm text-muted-foreground">Questions Monitored</p>
      </div>
      <div className="text-center">
        <p className="text-3xl font-bold text-green-600">{data.now_mentioned_in_ai}</p>
        <p className="text-sm text-muted-foreground">Now Cited in AI</p>
      </div>
      <div className="text-center">
        <p className={`text-3xl font-bold ${Number(data.conversion_pct) > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
          {Number(data.conversion_pct || 0).toFixed(1)}%
        </p>
        <p className="text-sm text-muted-foreground">Conversion Rate</p>
      </div>
    </div>
  );
}

function ClusterAuthorityTable({ rows }: { rows: ClusterAuthorityRow[] }) {
  if (!rows || rows.length === 0) {
    return <EmptyState message="No cluster-level data yet. Run SOV checks to start tracking cluster authority." />;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Cluster</TableHead>
          <TableHead className="text-right">Value (EUR)</TableHead>
          <TableHead className="text-right">Checks</TableHead>
          <TableHead className="text-right">Mentions</TableHead>
          <TableHead className="text-right">Mention Rate</TableHead>
          <TableHead className="text-right">Avg Quality</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.cluster_id}>
            <TableCell className="font-medium max-w-[250px] truncate">{row.cluster_label}</TableCell>
            <TableCell className="text-right">{Number(row.total_commercial_value_eur).toLocaleString('en', { maximumFractionDigits: 0 })}</TableCell>
            <TableCell className="text-right">{row.checks}</TableCell>
            <TableCell className="text-right">{row.mentions}</TableCell>
            <TableCell className="text-right">
              <MentionRateBadge rate={Number(row.mention_rate_pct)} />
            </TableCell>
            <TableCell className="text-right">
              {row.avg_citation_quality ? `${Number(row.avg_citation_quality).toFixed(1)}/3` : '-'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CompetitorDisplacementTable({ rows }: { rows: CompetitorDisplacementRow[] }) {
  if (!rows || rows.length === 0) {
    return <EmptyState message="No competitor displacement data yet. Add competitor-owned questions to the basket and run checks." />;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Competitor</TableHead>
          <TableHead className="text-right">Checks</TableHead>
          <TableHead className="text-right">Flipped to Us</TableHead>
          <TableHead className="text-right">Both Cited</TableHead>
          <TableHead className="text-right">They Hold</TableHead>
          <TableHead className="text-right">Flip Rate</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.competitor_domain}>
            <TableCell className="font-medium">{row.competitor_domain}</TableCell>
            <TableCell className="text-right">{row.total_checks}</TableCell>
            <TableCell className="text-right text-green-600 font-medium">{row.flipped_to_us}</TableCell>
            <TableCell className="text-right text-yellow-600">{row.both_mentioned}</TableCell>
            <TableCell className="text-right text-red-600">{row.competitor_holds}</TableCell>
            <TableCell className="text-right">
              <MentionRateBadge rate={Number(row.flip_rate_pct)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function BasketTable({ queries }: { queries: SOVBasketQuery[] }) {
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Query</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead>Coverage</TableHead>
            <TableHead>Funnel</TableHead>
            <TableHead>Cadence</TableHead>
            <TableHead className="text-right">Priority</TableHead>
            <TableHead className="text-right">Checks</TableHead>
            <TableHead>Last Checked</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {queries.map((q) => (
            <TableRow key={q.id}>
              <TableCell className="max-w-[300px] truncate font-medium" title={q.query_text}>
                {q.query_text}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {TIER_LABELS[q.tier] || q.tier}
                </Badge>
              </TableCell>
              <TableCell>
                <ProximityBadge value={Number(q.aeo_proximity) || 0} title={q.nearest_product_title} />
              </TableCell>
              <TableCell>
                <Badge
                  className="text-xs"
                  style={{ backgroundColor: FUNNEL_COLORS[q.funnel_stage || ''] || '#94a3b8', color: 'white' }}
                >
                  {q.funnel_stage || '-'}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">{CADENCE_LABELS[q.check_frequency] || q.check_frequency}</TableCell>
              <TableCell className="text-right text-sm">{Number(q.priority_score).toFixed(1)}</TableCell>
              <TableCell className="text-right text-sm">{q.check_count}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {q.last_checked_at ? new Date(q.last_checked_at).toLocaleDateString() : 'Never'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ============================================================================
// Utility Components
// ============================================================================

function ProximityBadge({ value, title }: { value: number; title?: string | null }) {
  const color = value >= 0.55 ? 'bg-green-100 text-green-800'
    : value >= 0.45 ? 'bg-blue-100 text-blue-800'
    : value >= 0.30 ? 'bg-yellow-100 text-yellow-800'
    : 'bg-gray-100 text-gray-600';
  const label = value >= 0.55 ? 'Strong' : value >= 0.45 ? 'Covered' : value >= 0.30 ? 'Weak' : 'Gap';
  return (
    <Badge className={`${color} text-xs cursor-default`} title={title ? `Nearest: ${title}` : undefined}>
      {label} ({value.toFixed(2)})
    </Badge>
  );
}

function MentionRateBadge({ rate }: { rate: number }) {
  const color = rate >= 30 ? 'bg-green-100 text-green-800'
    : rate >= 10 ? 'bg-yellow-100 text-yellow-800'
    : rate > 0 ? 'bg-orange-100 text-orange-800'
    : 'bg-gray-100 text-gray-600';
  return <Badge className={`${color} text-xs`}>{Number(rate).toFixed(1)}%</Badge>;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-8 text-center text-sm text-muted-foreground">
      <AlertCircle className="h-6 w-6 mx-auto mb-2 opacity-50" />
      {message}
    </div>
  );
}

function ZeroState({ onBuildBasket, building, basketTotal }: {
  onBuildBasket: () => void;
  building: boolean;
  basketTotal: number;
}) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No SOV data yet</h3>
        <p className="text-sm text-muted-foreground mb-1 max-w-md mx-auto">
          AI Share of Voice tracks how often ChatGPT, Perplexity, Gemini, and Claude
          mention your site when answering questions about your products and categories.
        </p>
        <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
          {basketTotal > 0
            ? `You have ${basketTotal} queries in your basket. Run checks to start collecting data.`
            : 'Start by building a monitoring basket — we\'ll select the top 200 queries from your demand signals.'}
        </p>
        <div className="flex gap-3 justify-center">
          {basketTotal === 0 && (
            <Button onClick={onBuildBasket} disabled={building}>
              {building ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
              Build Monitoring Basket
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}><CardContent className="pt-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
        ))}
      </div>
      <Skeleton className="h-[300px] w-full" />
    </div>
  );
}
